<template>
  <v-expansion-panels>
    <v-expansion-panel>
      <v-expansion-panel-title>
        <h3>Summary</h3>
      </v-expansion-panel-title>
      <v-expansion-panel-text
        class="tw-flex-inline"
        v-if="summary.category.unique.length > 0"
      >
        <v-row>
          <v-col :cols="4">
            <v-checkbox
              v-for="(uniqueCategory, index) in summary.category.unique"
              :key="index"
              :density="'compact'"
              :label="
                uniqueCategory +
                ': $' +
                summary.category.sum[index].total.toFixed(2)
              "
              :hide-details="true"
              v-model="summary.category.selected[index]"
              @change="emitFilter"
            />
          </v-col>
          <v-col :cols="4">
            <v-checkbox
              v-for="(uniqueBCJL, index) in summary.BCJL.unique"
              :key="index"
              :density="'compact'"
              :label="
                uniqueBCJL + ': $' + summary.BCJL.sum[index].total.toFixed(2)
              "
              :hide-details="true"
              v-model="summary.BCJL.selected[index]"
              @change="emitFilter"
            />
          </v-col>
          <v-col :cols="4">
            <v-list>
              <v-list-item>Total: {{ totalSum.toFixed(2) }}</v-list-item>
            </v-list>
          </v-col>
        </v-row>
        <v-row>
          <v-col :cols="6">
            <vue-apex-charts
              type="pie"
              height="250px"
              width="400px"
              :options="chartOptionsCategory"
              :series="seriesCategory"
            />
          </v-col>
          <v-col :cols="6">
            <vue-apex-charts
              type="pie"
              height="250px"
              width="400px"
              :options="chartOptionsBCJL"
              :series="seriesBCJL"
            />
          </v-col>
        </v-row>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, watchEffect } from "vue";
import { Transaction, Sum, Summary, Selection } from "../data-types";
import VueApexCharts from "vue3-apexcharts";
import { calculateOwe } from "../util/calculate-data";

export default defineComponent({
  name: "SpendingSummary",
  props: {
    tableData: { type: Array as PropType<Transaction[]>, default: () => [] },
  },

  components: {
    VueApexCharts,
  },
  setup(props, { emit }) {
    const emitFilter = () => {
      emit("filterTableEmit", {
        filterCategory: chartOptionsCategory.value.labels,
        filterBCJL: chartOptionsBCJL.value.labels,
      });
    };

    const summary = ref<Summary>({
      category: new Selection([], [], []),
      BCJL: new Selection([], [], []),
      bank: new Selection([], [], []),
    });
    const totalSum = ref<number>(0);

    const oweAmount = ref<{
      bcOweJl: number;
      jlOweBc: number;
    }>({
      bcOweJl: 0,
      jlOweBc: 0,
    });
    const seriesCategory = ref<number[]>([]);
    const chartOptionsCategory = ref<{
      labels: string[];
    }>({
      labels: [],
    });
    const seriesBCJL = ref<number[]>([]);
    const chartOptionsBCJL = ref<{
      labels: string[];
    }>({
      labels: [],
    });

    const initializeData = () => {
      props.tableData.forEach((row) => {
        if (!summary.value.category.unique.includes(row.category)) {
          summary.value.category.unique.push(row.category);
          summary.value.category.sum.push({
            subject: row.category,
            total: 0,
          });
        }
        if (!summary.value.BCJL.unique.includes(row.BCJL)) {
          summary.value.BCJL.unique.push(row.BCJL);
          summary.value.BCJL.sum.push({
            subject: row.BCJL,
            total: 0,
          });
        }
        if (!summary.value.bank.unique.includes(row.bank)) {
          summary.value.bank.unique.push(row.bank);
          summary.value.bank.sum.push({
            subject: row.bank,
            total: 0,
          });
        }
      });
      if (summary.value.category.selected.length === 0) {
        selectAll(summary.value.category);
        selectAll(summary.value.BCJL);
        selectAll(summary.value.bank);
      }
    };
    const selectAll = (groupToSelectAll: Selection) => {
      groupToSelectAll.selected = Array(groupToSelectAll.unique.length).fill(
        true
      );
    };
    const selectSummary = () => {
      seriesCategory.value = [];
      chartOptionsCategory.value.labels = [];
      seriesBCJL.value = [];
      chartOptionsBCJL.value.labels = [];
      summary.value.category.sum.forEach((e) => (e.total = 0));
      summary.value.BCJL.sum.forEach((e) => (e.total = 0));
      summary.value.bank.sum.forEach((e) => (e.total = 0));
      totalSum.value = 0;
      props.tableData.forEach((row) => {
        if (
          summary.value.category.selected[
            summary.value.category.unique.indexOf(row.category)
          ] &&
          summary.value.BCJL.selected[
            summary.value.BCJL.unique.indexOf(row.BCJL)
          ] &&
          summary.value.bank.selected[
            summary.value.bank.unique.indexOf(row.bank)
          ]
        ) {
          summary.value.category.sum.forEach((category) => {
            if (category.subject === row.category) {
              category.total += row.amount;
            }
          });
          summary.value.BCJL.sum.forEach((BCJL) => {
            if (BCJL.subject === row.BCJL) {
              BCJL.total += row.amount;
            }
          });
          summary.value.bank.sum.forEach((bank) => {
            if (bank.subject === row.bank) {
              bank.total += row.amount;
            }
          });
          if (!row.BCJL.includes("Not Include")) {
            totalSum.value += row.amount;
          }
        }
      });
      // [key, value], index
      Object.values(summary.value).forEach((value) => {
        // console.log(Object.entries(summary.value));
        // console.log(Object.values(summary.value));
        // console.log(key);
        // console.log(value);
        // console.log(index);
        value.sum.sort(compare);
        value.unique.sort();
      });

      const newSeriesCat: number[] = [];
      const newOptionCat: { labels: string[] } = { labels: [] };

      summary.value.category.sum.forEach((category, index) => {
        if (category.total > 0 && summary.value.category.selected[index]) {
          newSeriesCat.push(category.total);
          newOptionCat.labels.push(category.subject);
        }
      });

      seriesCategory.value = newSeriesCat;
      chartOptionsCategory.value = newOptionCat;

      const newSeriesBCJL: number[] = [];
      const newOptionBCJL: { labels: string[] } = { labels: [] };

      summary.value.BCJL.sum.forEach((BCJL, index) => {
        if (BCJL.total > 0 && summary.value.BCJL.selected[index]) {
          newSeriesBCJL.push(BCJL.total);
          newOptionBCJL.labels.push(BCJL.subject);
        }
      });

      seriesBCJL.value = newSeriesBCJL;
      chartOptionsBCJL.value = newOptionBCJL;
      oweAmount.value = calculateOwe(summary.value);
    };
    const compare = (a: Sum, b: Sum) => {
      return a.subject < b.subject ? -1 : a.subject > b.subject ? 1 : 0;
    };

    watchEffect(initializeData);
    watchEffect(selectSummary);
    return {
      summary,
      totalSum,
      oweAmount,
      seriesCategory,
      chartOptionsCategory,
      seriesBCJL,
      chartOptionsBCJL,
      emitFilter,
    };
  },
});
</script>
<style>
.v-input--density-compact {
  --v-input-control-height: 1rem;
  --v-input-padding-top: 0.1rem;
}
</style>
